import { api } from '../api.ts';
import { escapeHtml, html, mount, raw } from '../html.ts';
import { formatDateTime, t } from '../i18n.ts';
import { canComment, isAdmin, lookups, state } from '../state.ts';
import { openFormDialog, toastError } from '../ui.ts';

const MENTION = /@\[([^\]\n]{1,200})\]\(user:[0-9a-f-]{36}\)/gi;

/** Escaped comment text with mention chips and line breaks. */
export function renderCommentBody(body: string) {
  const escaped = escapeHtml(body).replace(/@\[([^\]\n]{1,200})\]\(user:[0-9a-f-]{36}\)/gi, (_, name: string) => `<span class="pma-mention">@${name}</span>`);
  return raw(escaped.replace(/\n/g, '<br>'));
}

export function stripMentions(body: string): string {
  return body.replace(MENTION, (_, name: string) => `@${name}`);
}

/** Comment thread with a mention picker; no nested <form> (may live inside a dialog form). */
export async function renderComments(target: HTMLElement, entityType: string, entityId: string): Promise<void> {
  const [{ items }, members] = await Promise.all([
    api.get<{ items: any[] }>(`/comments?entity_type=${entityType}&entity_id=${entityId}`),
    lookups.members(),
  ]);
  const me = state.me?.user.id;
  const mentionable = members.filter((m) => m.id !== me);
  mount(
    target,
    html`<fieldset class="p-fieldset pma-comments"><legend class="p-label">${t('Kommentare ({n})', { n: items.length })}</legend>
      ${items.length ? html`<ol class="pma-comments__list">${items.map(
        (c) => html`<li class="pma-comment">
          <div class="pma-comment__meta"><strong>${c.author_name ?? t('Gelöschte Person')}</strong>
            <span class="pma-muted pma-small">${formatDateTime(c.created_at)}${c.edited_at ? ` · ${t('bearbeitet')}` : ''}</span>
            ${canComment() && (c.author_user_id === me || isAdmin()) ? html`<button type="button" class="p-button p-button--ghost p-button--sm" data-delete-comment="${c.id}" aria-label="${t('Kommentar löschen')}">×</button>` : ''}</div>
          <div class="pma-comment__body">${renderCommentBody(c.body)}</div>
        </li>`,
      )}</ol>` : html`<p class="pma-muted pma-small">${t('Noch keine Kommentare.')}</p>`}
      ${canComment() ? html`<div class="pma-comment-new">
        <label class="p-field"><span class="p-visually-hidden">${t('Neuer Kommentar')}</span>
          <textarea class="p-textarea" rows="3" data-comment-text placeholder="${
            mentionable.length ? t('Kommentar schreiben … mit „Erwähnen“ Personen benachrichtigen') : t('Kommentar schreiben …')
          }"></textarea></label>
        <div class="pma-inline-add">
          ${
            // Guests see no member list, so there is nobody to mention.
            mentionable.length
              ? html`<select class="p-select" data-p-size="sm" data-mention aria-label="${t('Person erwähnen')}">
            <option value="">${t('Erwähnen …')}</option>${mentionable.map((m) => html`<option value="${m.id}">${m.label}</option>`)}
          </select>`
              : ''
          }
          <button type="button" class="p-button p-button--sm" data-send-comment>${t('Kommentieren')}</button>
        </div></div>` : ''}
    </fieldset>`,
  );
  const textArea = target.querySelector<HTMLTextAreaElement>('[data-comment-text]');
  target.querySelector<HTMLSelectElement>('[data-mention]')?.addEventListener('change', (event) => {
    const select = event.target as HTMLSelectElement;
    const member = members.find((m) => m.id === select.value);
    if (member && textArea) {
      const token = `@[${member.label.replace(/[\]\n]/g, '')}](user:${member.id}) `;
      const at = textArea.selectionStart ?? textArea.value.length;
      textArea.value = textArea.value.slice(0, at) + token + textArea.value.slice(at);
      textArea.focus();
    }
    select.value = '';
  });
  target.onclick = async (event) => {
    const element = event.target as HTMLElement;
    try {
      if (element.closest('[data-send-comment]') && textArea?.value.trim()) {
        await api.post('/comments', { entity_type: entityType, entity_id: entityId, body: textArea.value.trim() });
        await renderComments(target, entityType, entityId);
      }
      const remove = element.closest<HTMLElement>('[data-delete-comment]')?.dataset.deleteComment;
      if (remove) {
        await api.delete(`/comments/${remove}`);
        await renderComments(target, entityType, entityId);
      }
    } catch (error) {
      toastError(error);
    }
  };
}

/** Read-and-comment view of a record for people who cannot edit it (guests). */
export function openCommentThread(entityType: string, entityId: string, title: string): void {
  const dialog = openFormDialog({
    title,
    wide: true,
    fields: html`<section class="pma-span-2" data-thread></section>`,
    submitLabel: t('Schließen'),
    onSubmit: async () => {},
    // Nothing to cancel: the thread saves each comment on its own.
    onOpen: (d) => d.querySelector('[data-cancel]')?.remove(),
  });
  void renderComments(dialog.querySelector<HTMLElement>('[data-thread]')!, entityType, entityId);
}
